import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface StriveSimulationPayload {
  loan_amount: string | number;
  annual_rate?: string | number;
  monthly_rate?: string | number;
  loan_term?: string | number;
  loan_payment?: string | number;
  capital_payment_amount?: string | number;
  capital_payment_month?: string | number;
  second_payment_amount?: string | number;
  additional_payment_month?: string | number;
  third_payment_amount?: string | number;
  third_capital_payment_month?: string | number;
}

export interface StriveSimulationResponse {
  ok: boolean;
  msg: string;
  amortizacion?: {
    prestamo: string;
    tasaAnualEA: string;
    tasaMensualEM: string;
    plazo: number;
    cuota: string;
    totalPago: number;
    totalCapital: number;
    totalIntereses: number;
    amortizacion: any[] | null;
    // formatAbonosCapital (backend_v1/utils/flows/striveCol/flowStriveCol.js)
    // devuelve un objeto { "<mes>": "texto formateado" }, no un array.
    abonosCapital: Record<string, string>;
  };
}

export interface PriceSimulatorPayload {
  language?: string;
  business_category?: string;
  offer_type: 'products' | 'services';
  product_type?: 'elaborated' | 'purchased';
  elab_materials?: string | number;
  elab_hours?: string | number;
  elab_value_hour?: string | number;
  purchased_cost?: string | number;
  purchased_hours?: string | number;
  purchased_value_hour?: string | number;
  service_hours?: string | number;
  service_value_hour?: string | number;
  elab_rent_option?: 'yes' | 'no';
  elab_rent_monthly?: string | number;
  elab_rent_hours?: string | number;
  purchased_rent_option?: 'yes' | 'no';
  purchased_rent_monthly?: string | number;
  purchased_rent_hours?: string | number;
  rent_yes_no?: 'yes' | 'no';
  rent_monthly?: string | number;
  rent_hours?: string | number;
  other_cost_1_name?: string;
  other_cost_1_value?: string | number;
  other_cost_2_name?: string;
  other_cost_2_value?: string | number;
  other_cost_3_name?: string;
  other_cost_3_value?: string | number;
  other_cost_4_name?: string;
  other_cost_4_value?: string | number;
  final_margin: string | number;
}

export interface PriceSimulatorResponse {
  ok: boolean;
  msg: string;
  result?: {
    totalCost: string;
    margin: string;
    suggestedPrice: string;
    currency: string;
    language: string;
  };
  config?: any;
}

/**
 * Ver worker_flyers/helpers/getData.js (buildFlyerData/buildStructuredContent):
 * flyerPath debe tener el formato "flyer_<numero><letra>" — el número (1-4) es
 * el diseño y la letra (A/B/C/D) determina la categoría de plantilla
 * (A=Moda_Conf, B=Servicios_Hog, C=Belleza, D=Todo). El tester arma este
 * string automáticamente a partir de "categoría" + "diseño".
 */
export interface FlyerGeneratePayload {
  flyerPathName?: string;
  flyerPath: string;
  flyerSize: 'POST' | 'REELS';
  colorPrimaryPastel?: string;
  colorPrimary?: string;
  hasTitle?: boolean;
  title1?: string;
  hasSubtitle?: boolean;
  subtitle1?: string;
  textMain?: string;
  hasShortBullets?: boolean;
  shortBulletCount?: number;
  short_bullet_1?: string;
  short_bullet_2?: string;
  short_bullet_3?: string;
  short_bullet_4?: string;
  short_bullet_5?: string;
  short_bullet_6?: string;
  contactFacebook?: string;
  contactInstagram?: string;
  contactCity?: string;
  phoneList?: 'SI' | 'NO';
  enhancementdGpt?: 'YES' | 'NO';
  // WhatsApp media IDs reales (formato [{id, mime_type, sha256, file_name}]) —
  // solo se pueden obtener subiendo la imagen a través de un WhatsApp Flow real.
  photosFLayer_1?: string;
  photoslogo?: string;
}

export interface FlyerGenerateResponse {
  ok?: boolean;
  jobId?: string | number;
  message?: string;
  error?: string;
  details?: string;
}

export interface FlyerStatusResponse {
  ok: boolean;
  status: 'processing' | 'done' | 'failed' | 'not_found';
  url?: string;
  error?: string;
  at?: number;
}

export interface CimaTestResponse {
  ok: boolean;
  msg?: string;
  score?: number;
  group?: 'bajo' | 'medio' | 'alto';
  raw?: any;
}

@Injectable({
  providedIn: 'root',
})
export class FlowToolsService {
  public urlBackEnd: string = environment.base_url;

  constructor(private http: HttpClient) {}

  /**
   * Ejecuta la misma consulta que hace el WhatsApp Flow "Cima" (CIMA_TOKEN):
   * POST /chatBot/cima-test — vive en backend_v1 (no en chatbot_v1), reproduce
   * chatbot_v1/flows/CimaFlow.js (scoring + regla de grupo) SIN actualizar
   * ningún participante. Requiere CIMA_API_URL/CIMA_API_TOKEN en el .env de
   * backend_v1.
   */
  testCima(payload: Record<string, any>): Observable<CimaTestResponse> {
    return this.http
      .post<CimaTestResponse>(`${this.urlBackEnd}/chatBot/cima-test`, payload)
      .pipe(catchError(this.handleError));
  }

  /**
   * Ejecuta la misma lógica que el WhatsApp Flow "Strive" (STRIVE_CO_TOKEN):
   * POST /chatBot/flowstrive — sin JWT, igual que la llama el chatbot.
   */
  simulateStrive(payload: StriveSimulationPayload): Observable<StriveSimulationResponse> {
    return this.http
      .post<StriveSimulationResponse>(`${this.urlBackEnd}/chatBot/flowstrive`, {
        payload,
        incluirDetallesMes: true,
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Ejecuta la misma lógica que el WhatsApp Flow "Price Simulator" (PRICE_CO_TOKEN):
   * POST /chatBot/flowprice.
   */
  simulatePrice(payload: PriceSimulatorPayload): Observable<PriceSimulatorResponse> {
    return this.http
      .post<PriceSimulatorResponse>(`${this.urlBackEnd}/chatBot/flowprice`, { payload })
      .pipe(catchError(this.handleError));
  }

  /**
   * Encola la generación del flyer (FLYERS_TOKEN) en backend_v1 → worker_flyers
   * (cola Bull compartida 'flyerQueue' vía Redis, igual que /chatBot/generate-invoice
   * con 'invoiceQueue'). Responde solo {ok, jobId} — el resultado se consulta con
   * getFlyerStatus (POST /chatBot/generate-flyer + GET /chatBot/flyer-status/:phone).
   */
  generateFlyer(payload: FlyerGeneratePayload): Observable<FlyerGenerateResponse> {
    return this.http
      .post<FlyerGenerateResponse>(`${this.urlBackEnd}/chatBot/generate-flyer`, payload)
      .pipe(catchError(this.handleError));
  }

  /** Consulta el resultado de un flyer encolado con generateFlyer (poll). */
  getFlyerStatus(phone: string): Observable<FlyerStatusResponse> {
    return this.http
      .get<FlyerStatusResponse>(`${this.urlBackEnd}/chatBot/flyer-status/${phone}`)
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    console.error('HTTP Error:', error.status, error.error?.msg);
    return throwError(() => error);
  }
}
